/**
 * InsuranceDialog — Manage station insurance: view coverage status,
 * purchase new policies, and renew lapsed/expired insurance.
 *
 * Side-by-side layout: station list (left) | insurance detail (right).
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { Station, StationInsurance } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

const PAD = 10;

const STATUS_COLORS: Record<string, string> = {
  active: '#44ff88',
  lapsed: '#ffaa00',
  voided: '#ff4444',
  claimed: '#aa88ff',
  none: '#ff4444',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  lapsed: 'Lapsed',
  voided: 'Voided',
  claimed: 'Claimed',
  none: 'UNINSURED',
};

const STATION_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  waypoint: { label: 'Waypoint', color: '#00ffff' },
  depot: { label: 'Depot', color: '#00ff88' },
  fortress: { label: 'Fortress', color: '#ff8800' },
  capital: { label: 'Capital', color: '#ff0088' },
};

interface StationInsuranceEntry {
  station: Station;
  insurance: StationInsurance | null;
  currentPremium: number | null;
}

/** Premium cost breakdown fetched per-station */
interface PremiumDetails {
  currentPremium: number;
  storedValue: number;
  baseRate: number;
  defenseMultiplier: number;
  effectiveRate: number;
}

export class InsuranceDialog extends Phaser.GameObjects.Container {
  private mobile: boolean;
  private listW: number;
  private detailW: number;
  private monetization: MMOMonetizationManager;
  private entries: StationInsuranceEntry[] = [];
  private selectedEntry: StationInsuranceEntry | null = null;
  private premiumDetails: PremiumDetails | null = null;

  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;
  private isProcessing = false;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 560;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;
    const LIST_W = mobile ? dw - PAD * 2 : 260;
    const DETAIL_X = LIST_W + PAD * 2 + 10;
    this.listW = LIST_W;
    this.detailW = mobile ? dw - PAD * 2 : dw - DETAIL_X - PAD;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.insurance'),
      onClose: () => this.close(),
    });

    // Left: station list
    this.listContainer = scene.add.container(PAD, 58);
    this.add(this.listContainer);

    // Detail panel: overlay on mobile, side-by-side on desktop
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, 58);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    // Status text
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');
    const [stations, insurances] = await Promise.all([
      this.monetization.fetchStations(),
      this.monetization.fetchInsuranceList(),
    ]);

    const insuranceMap = new Map<string, StationInsurance>();
    for (const ins of insurances) {
      insuranceMap.set(ins.stationId, ins);
    }

    this.entries = [];
    for (const station of stations) {
      const insurance = insuranceMap.get(station.id) || null;
      this.entries.push({
        station,
        insurance,
        currentPremium: insurance ? insurance.effectiveRatePct : null,
      });
    }

    this.statusText.setText('');

    // Auto-select first station if available and nothing selected
    if (!this.selectedEntry && this.entries.length > 0) {
      this.selectedEntry = this.entries[0];
    }

    this.renderList();
    this.fetchAndRenderDetail();
  }

  /**
   * Fetch premium details for the currently selected station, then render.
   */
  private async fetchAndRenderDetail(): Promise<void> {
    if (!this.selectedEntry) {
      this.premiumDetails = null;
      this.renderDetail();
      return;
    }
    // Show loading state
    this.detailContainer.removeAll(true);
    const loadingText = this.scene.add.text(this.detailW / 2, 60, 'Loading...', {
      fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
    });
    loadingText.setOrigin(0.5, 0);
    this.detailContainer.add(loadingText);

    const data = await this.monetization.fetchInsuranceStatus(this.selectedEntry.station.id);
    this.premiumDetails = {
      currentPremium: data.currentPremium ?? 0,
      storedValue: data.storedValue,
      baseRate: data.baseRate,
      defenseMultiplier: data.defenseMultiplier,
      effectiveRate: data.effectiveRate,
    };
    this.renderDetail();
  }

  // ============ Station List (left column) ============

  private renderList(): void {
    this.listContainer.removeAll(true);
    let y = 0;

    const headingLabel = this.entries.length > 0
      ? `YOUR STATIONS: ${this.entries.length}`
      : 'YOUR STATIONS';
    const heading = this.scene.add.text(0, y, headingLabel, {
      fontSize: FontSize.md,
      color: CSSColors.textLight,
      fontFamily: Fonts.heading,
    });
    this.listContainer.add(heading);
    y += 26;

    if (this.entries.length === 0) {
      const empty = this.scene.add.text(0, y, 'No stations owned.\nPurchase a station first.', {
        fontSize: FontSize.sm,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      this.listContainer.add(empty);
      return;
    }

    for (const entry of this.entries) {
      const card = this.createStationCard(0, y, entry);
      this.listContainer.add(card);
      y += 68;
    }
  }

  private createStationCard(x: number, y: number, entry: StationInsuranceEntry): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const isSelected = this.selectedEntry?.station.id === entry.station.id;
    const cardH = 62;

    // Card background
    const cardBg = this.scene.add.graphics();
    cardBg.fillStyle(isSelected ? 0x253a66 : 0x161630, 0.8);
    cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    cardBg.lineStyle(1, isSelected ? 0x00cccc : Colors.cyan, isSelected ? 0.4 : 0.08);
    cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    card.add(cardBg);

    const typeInfo = STATION_TYPE_LABELS[entry.station.stationType] || { label: entry.station.stationType, color: '#ffffff' };

    // Station type
    const nameText = this.scene.add.text(10, 8, typeInfo.label, {
      fontSize: FontSize.sm,
      color: typeInfo.color,
      fontFamily: Fonts.heading,
    });
    card.add(nameText);

    // Insurance status
    const insuranceStatus = entry.insurance?.status || 'none';
    const statusColor = STATUS_COLORS[insuranceStatus] || '#667788';
    const statusLabel = STATUS_LABELS[insuranceStatus] || insuranceStatus;

    const statusText = this.scene.add.text(10, 28, statusLabel, {
      fontSize: FontSize.xs,
      color: statusColor,
      fontFamily: Fonts.heading,
    });
    card.add(statusText);

    // Premium rate or "No policy"
    if (entry.insurance) {
      const premiumText = this.scene.add.text(10, 44, `Rate: ${entry.insurance.effectiveRatePct.toFixed(1)}%`, {
        fontSize: FontSize.xs,
        color: '#889999',
        fontFamily: Fonts.body,
      });
      card.add(premiumText);
    } else {
      const noPremiumText = this.scene.add.text(10, 44, 'No policy', {
        fontSize: FontSize.xs,
        color: '#ff4444',
        fontFamily: Fonts.body,
      });
      card.add(noPremiumText);
    }

    // Position (right-aligned)
    if (entry.station.positionQ !== null && entry.station.positionR !== null) {
      const posText = this.scene.add.text(this.listW - 10, 8, `(${entry.station.positionQ},${entry.station.positionR})`, {
        fontSize: FontSize.xs,
        color: '#889999',
        fontFamily: Fonts.body,
      });
      posText.setOrigin(1, 0);
      card.add(posText);
    }

    // Station status (right-aligned)
    const stationStatusText = this.scene.add.text(this.listW - 10, 28, entry.station.status, {
      fontSize: FontSize.xs,
      color: '#667788',
      fontFamily: Fonts.body,
    });
    stationStatusText.setOrigin(1, 0);
    card.add(stationStatusText);

    // Interactive hit area with Zone
    const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) {
        cardBg.clear();
        cardBg.fillStyle(0x1e1e44, 0.9);
        cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
        cardBg.lineStyle(1, Colors.cyan, 0.15);
        cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      }
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      cardBg.clear();
      cardBg.fillStyle(isSelected ? 0x253a66 : 0x161630, 0.8);
      cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      cardBg.lineStyle(1, isSelected ? 0x00cccc : Colors.cyan, isSelected ? 0.4 : 0.08);
      cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedEntry = entry;
      if (this.mobile) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
      this.renderList();
      this.fetchAndRenderDetail();
    });
    card.add(hitZone);

    return card;
  }

  // ============ Detail Panel (right column) ============

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (this.mobile) {
      const backBtn = this.scene.add.text(0, 0, '\u2190 Back', {
        fontSize: FontSize.sm, color: '#00ffff', fontFamily: Fonts.body,
      }).setInteractive().on('pointerdown', () => {
        this.detailContainer.setVisible(false);
        this.listContainer.setVisible(true);
      });
      this.detailContainer.add(backBtn);
    }

    if (!this.selectedEntry) {
      const hint = this.scene.add.text(this.detailW / 2, 60, 'Select a station\nto view insurance', {
        fontSize: FontSize.sm,
        color: '#556677',
        fontFamily: Fonts.body,
        align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const { station, insurance } = this.selectedEntry;
    const typeInfo = STATION_TYPE_LABELS[station.stationType] || { label: station.stationType, color: '#ffffff' };

    let y = 0;

    // Title
    const titleText = this.scene.add.text(0, y, typeInfo.label, {
      fontSize: FontSize.xl,
      color: typeInfo.color,
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(titleText);
    y += 28;

    // Station info rows
    this.addInfoRow(0, y, 'Status', station.status, '#aaccff');
    y += 20;

    this.addInfoRow(0, y, 'HP', `${station.currentHp}/${station.maxHp}`, '#44ff88');
    y += 20;

    const storageColor = station.storedAsteroids >= station.maxStorage ? '#ff4444' : '#ffd700';
    this.addInfoRow(0, y, 'Storage', `${station.storedAsteroids}/${station.maxStorage}`, storageColor);
    y += 20;

    if (station.positionQ !== undefined && station.positionR !== undefined) {
      this.addInfoRow(0, y, 'Position', `(${station.positionQ}, ${station.positionR})`, '#889999');
      y += 20;
    }

    y += 4;

    // Divider
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.1);
    divider.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(divider);
    y += 10;

    // Insurance section header
    const insuranceHeader = this.scene.add.text(0, y, 'Insurance Details', {
      fontSize: FontSize.md,
      color: '#44aaff',
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(insuranceHeader);
    y += 24;

    if (insurance) {
      this.renderInsuredDetail(insurance, y);
    } else {
      this.renderUninsuredDetail(station, y);
    }
  }

  /** Render detail when the station HAS insurance */
  private renderInsuredDetail(insurance: StationInsurance, startY: number): void {
    let y = startY;

    // Insurance status
    const insStatus = insurance.status;
    const insStatusColor = STATUS_COLORS[insStatus] || '#667788';
    const insStatusLabel = STATUS_LABELS[insStatus] || insStatus;

    const insStatusText = this.scene.add.text(0, y, `Status: ${insStatusLabel}`, {
      fontSize: FontSize.sm,
      color: insStatusColor,
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(insStatusText);
    y += 22;

    // Daily cost (primary)
    const pd = this.premiumDetails;
    const dailyCost = pd ? pd.currentPremium : 0;
    this.addInfoRow(0, y, 'Daily Cost', `$${dailyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '#ffd700');
    y += 20;

    this.addInfoRow(0, y, 'Total Paid', `$${insurance.totalPremiumsPaid.toFixed(2)}`, '#889999');
    y += 20;

    this.addInfoRow(0, y, 'Claims Filed', `${insurance.claims}`, '#889999');
    y += 20;

    // Cost breakdown (muted subsection)
    y += 4;
    const breakdownHeader = this.scene.add.text(0, y, 'COST BREAKDOWN', {
      fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.heading,
    });
    this.detailContainer.add(breakdownHeader);
    y += 16;

    const storedVal = pd ? pd.storedValue : 0;
    this.addInfoRow(0, y, 'Stored Value', `$${storedVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '#778899');
    y += 18;

    this.addInfoRow(0, y, 'Base Rate', `${insurance.basePremiumRatePct.toFixed(1)}%`, '#778899');
    y += 18;

    this.addInfoRow(0, y, 'Defense Mult.', `${insurance.currentMultiplier.toFixed(1)}x`, '#778899');
    y += 20;

    // Coverage expiry
    if (insurance.coverageExpiresAt) {
      const expiryDate = new Date(insurance.coverageExpiresAt);
      const now = new Date();
      const isExpired = expiryDate < now;
      const expiryColor = isExpired ? '#ff4444' : '#44ff88';
      const expiryLabel = isExpired ? 'EXPIRED' : expiryDate.toLocaleString();

      this.addInfoRow(0, y, 'Coverage Until', expiryLabel, expiryColor);
      y += 20;
    }

    y += 10;

    // Action buttons based on status
    if (insStatus === 'lapsed' || insStatus === 'claimed') {
      this.detailContainer.add(
        this.createActionButton(0, y, 'Renew Insurance', 0x006644, () => this.handleRenew(this.selectedEntry!.station.id)),
      );
    }
  }

  /** Render detail when station has NO insurance — big red warning */
  private renderUninsuredDetail(station: Station, startY: number): void {
    let y = startY;

    // Big red "UNINSURED" warning
    const warningBg = this.scene.add.graphics();
    warningBg.fillStyle(0x441111, 0.6);
    warningBg.fillRoundedRect(0, y, this.detailW, 42, Radius.sm);
    warningBg.lineStyle(1, 0xff4444, 0.3);
    warningBg.strokeRoundedRect(0, y, this.detailW, 42, Radius.sm);
    this.detailContainer.add(warningBg);

    const warningText = this.scene.add.text(this.detailW / 2, y + 12, 'NOT INSURED', {
      fontSize: FontSize.lg,
      color: '#ff4444',
      fontFamily: Fonts.heading,
    });
    warningText.setOrigin(0.5, 0);
    this.detailContainer.add(warningText);
    y += 52;

    // Explanation
    const explainText = this.scene.add.text(0, y, 'This station has no insurance policy.\nStored asteroids are at risk if raided.', {
      fontSize: FontSize.sm,
      color: '#ff8888',
      fontFamily: Fonts.body,
      lineSpacing: 4,
    });
    this.detailContainer.add(explainText);
    y += 46;

    // Daily cost (primary)
    const pd = this.premiumDetails;
    const dailyCost = pd ? pd.currentPremium : 0;
    const costStr = dailyCost > 0
      ? `$${dailyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '$0.00';
    this.addInfoRow(0, y, 'Daily Cost', costStr, '#ffd700');
    y += 22;

    // Cost breakdown (muted subsection)
    const breakdownHeader = this.scene.add.text(0, y, 'COST BREAKDOWN', {
      fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.heading,
    });
    this.detailContainer.add(breakdownHeader);
    y += 16;

    const storedVal = pd ? pd.storedValue : 0;
    const baseRate = pd ? pd.baseRate : 3.0;
    const defMult = pd ? pd.defenseMultiplier : 1.0;

    this.addInfoRow(0, y, 'Stored Value', `$${storedVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '#778899');
    y += 18;

    this.addInfoRow(0, y, 'Base Rate', `${baseRate.toFixed(1)}%`, '#778899');
    y += 18;

    this.addInfoRow(0, y, 'Defense Mult.', `${defMult.toFixed(1)}x`, '#778899');
    y += 18;

    const infoText = this.scene.add.text(0, y, 'More defenders nearby = lower multiplier.', {
      fontSize: FontSize.xs,
      color: '#556677',
      fontFamily: Fonts.body,
    });
    this.detailContainer.add(infoText);
    y += 22;

    // Purchase button — prominent
    this.detailContainer.add(
      this.createActionButton(0, y, 'Purchase Insurance', 0x006644, () => this.handlePurchase(station.id)),
    );
  }

  // ============ Helpers ============

  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string = '#aaccdd'): void {
    const labelText = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.detailContainer.add(labelText);

    const valText = this.scene.add.text(x + labelText.width, y, value, {
      fontSize: FontSize.xs,
      color: valueColor,
      fontFamily: Fonts.body,
    });
    this.detailContainer.add(valText);
  }

  private createActionButton(
    x: number,
    y: number,
    label: string,
    color: number,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const btnW = this.detailW;
    const btnH = 32;

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(0, 0, btnW, btnH, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(0, 0, btnW, btnH, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(btnW / 2, btnH / 2, label, {
      fontSize: FontSize.sm,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x222222, 1);
      bg.fillRoundedRect(0, 0, btnW, btnH, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, btnW, btnH, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 0.8);
      bg.fillRoundedRect(0, 0, btnW, btnH, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.2);
      bg.strokeRoundedRect(0, 0, btnW, btnH, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }

  // ============ Actions ============

  private async handlePurchase(stationId: string): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.statusText.setText('Purchasing insurance...');
    try {
      const result = await this.monetization.purchaseInsurance(stationId);
      if (result.success) {
        this.statusText.setText(`Insurance purchased! Premium: $${result.premium?.toFixed(2) ?? '—'}`);
        this.selectedEntry = null;
        this.loadData();
      } else {
        this.statusText.setText(result.error || 'Insurance purchase failed');
      }
    } catch {
      this.statusText.setText('Insurance purchase failed');
    } finally {
      this.isProcessing = false;
    }
  }

  private async handleRenew(stationId: string): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.statusText.setText('Renewing insurance...');
    try {
      const result = await this.monetization.renewInsurance(stationId);
      if (result.success) {
        this.statusText.setText(`Insurance renewed! Premium: $${result.premium?.toFixed(2) ?? '—'}`);
        this.selectedEntry = null;
        this.loadData();
      } else {
        this.statusText.setText(result.error || 'Insurance renewal failed');
      }
    } catch {
      this.statusText.setText('Insurance renewal failed');
    } finally {
      this.isProcessing = false;
    }
  }
}
